// frontend/src/hooks/useCareerPath.ts

import { useEffect, useState, useCallback } from 'react';
import { careerService, CareerApiError } from '../services/career.service';
import { useAuthStore } from '../store/authStore';
import { CareerPath, ActiveCareerPath, CareerStep } from '../types/career.types';

export function useCareerPath() {
  const userId = useAuthStore((s) => s.session?.user.id);

  const [careerPaths, setCareerPaths] = useState<CareerPath[]>([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(true);

  const [selectedCareer, setSelectedCareer] = useState<CareerPath | null>(null);
  const [activePath, setActivePath] = useState<ActiveCareerPath | null>(null);
  const [isLoadingActivePath, setIsLoadingActivePath] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    careerService
      .listCareerPaths()
      .then(setCareerPaths)
      .catch((err) => setError(err instanceof Error ? err.message : 'Kariyer yolları yüklenemedi.'))
      .finally(() => setIsLoadingPaths(false));
  }, []);

  const generatePathForCareer = useCallback(async (career: CareerPath) => {
    setIsGenerating(true);
    setError(null);
    try {
      const result = await careerService.generatePath(career.id);
      const steps: CareerStep[] = result.steps.map((s, i) => ({
        title: s.title,
        description: s.description,
        durationMonths: s.durationMonths,
        status: i === 0 ? 'current' : 'locked',
      }));
      setActivePath({ pathId: result.pathId, careerTitle: result.careerTitle, steps });
    } catch (err) {
      setError(err instanceof CareerApiError ? err.message : 'Kariyer yolu oluşturulamadı.');
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const selectCareer = useCallback(
    async (career: CareerPath) => {
      setSelectedCareer(career);
      setActivePath(null);
      setError(null);
      setIsLoadingActivePath(true);

      try {
        await generatePathForCareer(career);
      } finally {
        setIsLoadingActivePath(false);
      }
    },
    [generatePathForCareer]
  );

  const generatePath = useCallback(async () => {
    if (!selectedCareer) return;
    await generatePathForCareer(selectedCareer);
  }, [selectedCareer, generatePathForCareer]);

  return {
    careerPaths,
    isLoadingPaths,
    selectedCareer,
    selectCareer,
    activePath,
    isLoadingActivePath,
    isGenerating,
    generatePath,
    error,
  };
}
